// ─── Secure Chat slide-out drawer ───────────────────────────────────────────
// See CHAT_E2E_DESIGN.md §8: "Teams-style sidebar chat... slide-out panel:
// thread list (DMs + Groups) on the left, active conversation on the right."
// Originally a small dropdown anchored under the header's chat icon — that
// made it cramped for real conversations and easy to lose behind other UI.
// Now a full-height drawer that slides in from the right edge of the screen
// (with a small collapse tab on its edge to push it back out), and an
// "expand to full screen" toggle for when you want the whole window.
// Mounted by Header.js, wrapped in <ChatKeyGate> so the first-run/unlock
// modal overlays on top of this panel without preventing it from rendering
// underneath.

import React, { useEffect, useMemo, useState } from 'react';
import { useCRM } from '../../context/CRMContext';
import { useChatIdentity } from '../../context/ChatIdentityContext';
import {
  subscribeMyChatThreads,
  createDmThread,
  createGroupThread,
  markChatThreadRead,
} from '../../firebase/firestore';
import NewChatModal from './NewChatModal';
import ConversationView from './ConversationView';

export default function ChatPanel({ onClose }) {
  const { currentUser, users, getUserById } = useCRM();
  // ChatPanel always renders inside <ChatKeyGate>, which mounts
  // ChatIdentityProvider above it (see ChatKeyGate.js), so this context read
  // is always available here. `status` may be anything other than 'ready'
  // while the first-run/unlock modal is up — ConversationView treats that as
  // "can't decrypt/send yet," not as a reason this panel shouldn't render.
  const { status: chatKeyStatus, identity } = useChatIdentity();
  const companyId = currentUser?.companyId;
  const userId = currentUser?.id;

  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [showNewChat, setShowNewChat] = useState(false);
  const [fullScreen, setFullScreen] = useState(false);

  useEffect(() => {
    if (!companyId || !userId) { setThreads([]); return; }
    const unsub = subscribeMyChatThreads(companyId, userId, setThreads);
    return unsub;
  }, [companyId, userId]);

  const activeThread = useMemo(() => threads.find(t => t.id === activeThreadId) || null, [threads, activeThreadId]);

  const isUnread = (thread) => {
    if (!thread.lastMessageAt) return false;
    if (thread.lastMessageSenderId === userId) return false; // your own last message
    const lastRead = thread.lastReadAt?.[userId];
    if (!lastRead) return true;
    return (thread.lastMessageAt.seconds || 0) > (lastRead.seconds || 0);
  };

  const openThread = (threadId) => {
    setActiveThreadId(threadId);
    markChatThreadRead(companyId, threadId, userId);
  };

  const handleCreateDm = async (otherUserId) => {
    const threadId = await createDmThread(companyId, userId, otherUserId);
    setShowNewChat(false);
    openThread(threadId);
  };

  const handleCreateGroup = async (memberIds, name) => {
    const threadId = await createGroupThread(companyId, userId, memberIds, name);
    setShowNewChat(false);
    openThread(threadId);
  };

  const threadLabel = (thread) => {
    if (thread.type === 'group') return thread.name || 'Group chat';
    const otherId = (thread.memberIds || []).find(id => id !== userId);
    const other = otherId ? getUserById(otherId) : null;
    return other?.name || other?.email || 'Direct message';
  };

  return (
    <div
      className="chat-drawer"
      style={fullScreen ? {
        position: 'fixed', top: 0, right: 0, bottom: 0, left: 0, width: '100vw', height: '100vh',
        background: '#ffffff', border: 'none', borderRadius: 0, overflow: 'hidden', zIndex: 200,
        boxShadow: 'none', display: 'flex', flexDirection: 'column',
      } : {
        position: 'fixed', top: 0, right: 0, bottom: 0, width: 780, maxWidth: '96vw', height: '100vh',
        background: '#ffffff', border: '1px solid rgba(0,0,0,0.1)', borderRight: 'none',
        borderRadius: '16px 0 0 16px', overflow: 'hidden', zIndex: 200,
        boxShadow: '-24px 0 80px rgba(0,0,0,0.22)',
        display: 'flex', flexDirection: 'column',
      }}
    >
      <style>{`
        @keyframes chatDrawerSlideIn { from { transform: translateX(100%); } to { transform: translateX(0); } }
        .chat-drawer { animation: chatDrawerSlideIn 0.22s cubic-bezier(0.16, 1, 0.3, 1); }
      `}</style>

      {/* Small collapse tab — clicking it slides the drawer back out to the
          right edge of the screen, same idea as the docked tab that opens it
          in the first place (see Header.js). Only shown in drawer mode; in
          full screen there's nowhere for it to "dock" to visually. */}
      {!fullScreen && (
        <button
          onClick={onClose}
          title="Collapse chat"
          style={{
            position: 'absolute', top: '50%', left: -18, transform: 'translateY(-50%)',
            width: 18, height: 56, borderRadius: '8px 0 0 8px', border: '1px solid rgba(0,0,0,0.1)',
            borderRight: 'none', background: '#ffffff', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '-4px 0 12px rgba(0,0,0,0.08)', color: '#7c3aed', padding: 0,
          }}
        >
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
        </button>
      )}

      {/* Panel header */}
      <div style={{
        padding: '12px 16px', borderBottom: '1px solid var(--border)',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0,
      }}>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--text-primary)' }}>Chat</span>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <button onClick={() => setFullScreen(f => !f)} className="btn-ghost" title={fullScreen ? 'Exit full screen' : 'Open full screen'} style={{ width: 30, height: 30, borderRadius: 8, padding: 6 }}>
            {fullScreen ? (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3v3a2 2 0 0 1-2 2H3"/><path d="M21 8h-3a2 2 0 0 1-2-2V3"/><path d="M3 16h3a2 2 0 0 1 2 2v3"/><path d="M16 21v-3a2 2 0 0 1 2-2h3"/></svg>
            ) : (
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3"/><path d="M21 8V5a2 2 0 0 0-2-2h-3"/><path d="M3 16v3a2 2 0 0 0 2 2h3"/><path d="M16 21h3a2 2 0 0 0 2-2v-3"/></svg>
            )}
          </button>
          <button onClick={onClose} className="btn-ghost" title="Close chat" style={{ width: 30, height: 30, borderRadius: 8, padding: 6 }}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
        </div>
      </div>

      <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
        {/* Thread list */}
        <div style={{ width: 290, borderRight: '1px solid var(--border)', display: 'flex', flexDirection: 'column', flexShrink: 0 }}>
          <div style={{ padding: '10px 12px', flexShrink: 0 }}>
            <button className="btn-primary" onClick={() => setShowNewChat(true)} style={{ width: '100%', justifyContent: 'center', fontSize: 12.5, padding: '8px 10px' }}>
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
              New chat
            </button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto' }}>
            {threads.length === 0 && (
              <div style={{ padding: '20px 16px', textAlign: 'center', fontSize: 12, color: 'var(--text-muted)' }}>
                No conversations yet. Start one above.
              </div>
            )}
            {threads.map(thread => {
              const unread = isUnread(thread);
              const active = thread.id === activeThreadId;
              const label = threadLabel(thread);
              const initials = (label || '?').split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase();
              return (
                <div
                  key={thread.id}
                  onClick={() => openThread(thread.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', cursor: 'pointer',
                    background: active ? 'var(--color-primary-soft)' : 'transparent',
                    borderBottom: '1px solid rgba(0,0,0,0.04)',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.03)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  <div className="avatar" style={{ background: thread.type === 'group' ? '#3b82f6' : '#7c3aed', color: '#fff', width: 32, height: 32, fontSize: 12, flexShrink: 0 }}>
                    {thread.type === 'group' ? (
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>
                    ) : initials}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: unread ? 700 : 500, color: 'var(--text-primary)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                      {label}
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {thread.type === 'group' ? `${(thread.memberIds || []).length} members` : 'Direct message'}
                    </div>
                  </div>
                  {unread && <span style={{ width: 8, height: 8, borderRadius: '50%', background: '#ef4444', flexShrink: 0 }} />}
                </div>
              );
            })}
          </div>
        </div>

        {/* Active conversation */}
        <ConversationView
          companyId={companyId}
          thread={activeThread}
          currentUserId={userId}
          getUserById={getUserById}
          chatKeyStatus={chatKeyStatus}
          identity={identity}
        />
      </div>

      {showNewChat && (
        <NewChatModal
          users={users}
          currentUserId={userId}
          onCreateDm={handleCreateDm}
          onCreateGroup={handleCreateGroup}
          onClose={() => setShowNewChat(false)}
        />
      )}
    </div>
  );
}
